import { LegacyProductCode } from '@up2stay/up2promo-api-client';
import { useState } from 'react';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { WizardShell } from '@/core/components/onboarding/wizard-shell';
import { PageHeader } from '@/core/components/page-header';
import { ProductScanModal } from '@/core/components/product-scan-modal';
import { ProductHashOutcomePanel } from '@/core/components/products/product-hash-outcome-panel';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { isCapacitorNative } from '@/core/utils/capacitor.utils';
import { ConfigurePhase } from '@/routes/app/products-setup-wizard/products-setup-configure-flow';
import { ProductsSetupStepCreateLocation } from '@/routes/app/products-setup-wizard/ProductsSetupStepCreateLocation';
import { ProductsSetupStepCustomUrl } from '@/routes/app/products-setup-wizard/ProductsSetupStepCustomUrl';
import { ProductsSetupStepDescription } from '@/routes/app/products-setup-wizard/ProductsSetupStepDescription';
import { ProductsSetupStepDestination } from '@/routes/app/products-setup-wizard/ProductsSetupStepDestination';
import { ProductsSetupStepPickLocation } from '@/routes/app/products-setup-wizard/ProductsSetupStepPickLocation';
import { ProductsSetupStepProfile } from '@/routes/app/products-setup-wizard/ProductsSetupStepProfile';
import { ProductsSetupStepScan } from '@/routes/app/products-setup-wizard/ProductsSetupStepScan';
import { ProductsSetupStepSelectType } from '@/routes/app/products-setup-wizard/ProductsSetupStepSelectType';
import { ProductsSetupStepUsername } from '@/routes/app/products-setup-wizard/ProductsSetupStepUsername';
import { ProductsSetupWizardDialogs } from '@/routes/app/products-setup-wizard/products-setup-wizard-dialogs';
import type { ProductsSetupWizardPageProps } from '@/routes/app/products-setup-wizard/products-setup-wizard-types';
import { useProductsSetupWizard } from '@/routes/app/products-setup-wizard/use-products-setup-wizard';
import { AppIonPage } from '@/core/components/app-ion-page';

export type {
  ProductSetupWizardVariant,
  ProductsSetupWizardPageProps,
} from '@/routes/app/products-setup-wizard/products-setup-wizard-types';

function ProductsSetupBreadcrumb({
  domainLabel,
  leafLabel,
}: {
  domainLabel: string;
  leafLabel: string;
}) {
  return (
    <>
      {domainLabel}
      <span className="mx-1.5 text-muted-foreground/70">/</span>
      {leafLabel}
    </>
  );
}

export function ProductsSetupWizardPage({
  variant = 'workspace',
}: ProductsSetupWizardPageProps) {
  const wizard = useProductsSetupWizard(variant);
  const [manualCodeOpen, setManualCodeOpen] = useState(false);
  const dict = useDictionary();
  const {
    productsSetupDict,
    isCreateLocationPhase,
    currentStep,
    configurePhase,
    isNative,
    fallbackPath,
    isProfileBcMode,
  } = wizard;

  const breadcrumb = (
    <ProductsSetupBreadcrumb
      domainLabel={dict.nav.navDomainWork}
      leafLabel={isProfileBcMode ? dict.nav.profiles : dict.nav.products}
    />
  );

  const showBack = isCreateLocationPhase || currentStep > 1;

  return (
    <>
      <AppIonPage>
        <AppPageContent scrollY={false}>
          <div className="relative flex h-full min-h-0 flex-col">
            <AppPageLayout
              withGap={false}
              className="flex min-h-0 min-w-0 flex-1 flex-col"
            >
              <PageHeader
                breadcrumb={breadcrumb}
                title={productsSetupDict?.title ?? ''}
                onBack={wizard.handleAbortRequest}
              />
              {/* Same shell chrome as CreateWorkspacePage — page gutters own
                  Continue width; stage max-width stays on step content only. */}
              <WizardShell
                currentStep={wizard.shellCurrentStep}
                totalSteps={wizard.shellTotalSteps}
                stepLabels={
                  wizard.stepLabels.length === wizard.shellTotalSteps
                    ? wizard.stepLabels
                    : undefined
                }
                direction={wizard.direction}
                stepKey={wizard.shellStepKey}
                onBack={showBack ? wizard.handleShellBack : undefined}
                onNext={
                  wizard.showShellNext ? wizard.handleShellNext : undefined
                }
                nextLabel={wizard.nextLabel}
                nextDisabled={wizard.nextDisabled}
                isLoading={
                  isCreateLocationPhase
                    ? wizard.isCreatingLocation
                    : wizard.createMutation.isPending
                }
                showBack={showBack}
                variant="shell"
                contentAlignment="center"
                contentClassName="mx-auto w-full max-w-xl md:max-w-3xl lg:max-w-4xl"
              >
                {isCreateLocationPhase ? (
                  <ProductsSetupStepCreateLocation
                    name={wizard.locationName}
                    onNameChange={(value) => {
                      wizard.setLocationName(value);
                      if (value.trim() && wizard.locationNameError) {
                        wizard.setLocationNameError(null);
                      }
                    }}
                    nameError={wizard.locationNameError}
                    addressDetails={wizard.locationAddressDetails}
                    onAddressDetailsChange={wizard.setLocationAddressDetails}
                    latitude={wizard.locationLatitude}
                    longitude={wizard.locationLongitude}
                    onCoordinatesChange={(lat, lng) => {
                      wizard.setLocationLatitude(lat);
                      wizard.setLocationLongitude(lng);
                    }}
                    onPlaceSelect={wizard.handlePlaceSelect}
                    disabled={wizard.isCreatingLocation}
                    labels={wizard.createLocationLabels}
                  />
                ) : null}

                {!isCreateLocationPhase && currentStep === 1 ? (
                  <ProductsSetupStepScan
                    hashes={wizard.hashes}
                    removeHash={wizard.removeHash}
                    error={wizard.error}
                    scanProps={{
                      onScanQr: wizard.handleScanQr,
                      onScanNfc:
                        isNative && wizard.startNfcScan
                          ? wizard.handleScanNfc
                          : undefined,
                      isQrBusy: isNative
                        ? wizard.isQrScanning
                        : wizard.showScanModal,
                      isNfcBusy: wizard.isNfcScanning,
                      disabled: wizard.isResolvingHash,
                      showNfc: isNative && !!wizard.startNfcScan,
                    }}
                    manualEntry={{
                      open: manualCodeOpen,
                      onOpenChange: setManualCodeOpen,
                      onSubmit: (result) => {
                        void wizard.handleScanResult(result);
                      },
                    }}
                    outcomeSlot={
                      wizard.pendingResolution || wizard.isResolvingHash ? (
                        <ProductHashOutcomePanel
                          // Fresh claim state per scanned hash (DEV-756).
                          key={wizard.pendingResolution?.hash ?? ''}
                          resolution={
                            wizard.pendingResolution ?? {
                              kind: 'error',
                              hash: '',
                            }
                          }
                          isChecking={
                            wizard.isResolvingHash && !wizard.pendingResolution
                          }
                          onDismiss={() => wizard.setPendingResolution(null)}
                          onGoToProducts={() =>
                            wizard.navRedirect(fallbackPath)
                          }
                        />
                      ) : null
                    }
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.SelectType ? (
                  <ProductsSetupStepSelectType
                    includeBusinessCard={!isProfileBcMode}
                    onSelect={wizard.handleSelectProductType}
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.Destination ? (
                  <ProductsSetupStepDestination
                    productType={wizard.batchProductTypeFromScan}
                    onChoose={wizard.handleChooseDestination}
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.Location ? (
                  <ProductsSetupStepPickLocation
                    productType={wizard.batchProductTypeFromScan}
                    targetKind={wizard.setupFormValues.targetKind}
                    locationId={wizard.setupFormValues.locationId}
                    onLocationChange={wizard.setSetupLocationId}
                    locations={wizard.formLocations}
                    onCreateLocation={wizard.openCreateLocation}
                    onUseCustomUrl={wizard.handleSwitchToCustomUrl}
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.Username ? (
                  <ProductsSetupStepUsername
                    productType={wizard.batchProductTypeFromScan}
                    value={wizard.usernameDraft}
                    onChange={wizard.handleUsernameChange}
                    onUseCustomUrl={wizard.handleSwitchToCustomUrl}
                    error={wizard.usernameError}
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.CustomUrl ? (
                  <ProductsSetupStepCustomUrl
                    productType={wizard.batchProductTypeFromScan}
                    value={wizard.setupFormValues.targetUrl}
                    onChange={wizard.setSetupTargetUrl}
                    invalidMessage={wizard.invalidTargetUrlMessage}
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.Profile ? (
                  <ProductsSetupStepProfile
                    workspaceId={wizard.currentWorkspaceId}
                    userId={wizard.setupFormValues.userId}
                    onUserChange={wizard.setSetupUserId}
                    hideProfileUserPicker={wizard.isProfileBcMode}
                    lockedProfileUserLabel={
                      wizard.profilesLinkCardDict?.linkedToSelf
                    }
                  />
                ) : null}

                {!isCreateLocationPhase &&
                currentStep === 2 &&
                configurePhase === ConfigurePhase.Description ? (
                  <ProductsSetupStepDescription
                    value={wizard.setupFormValues.description}
                    onChange={wizard.setSetupDescription}
                    isBusinessCard={
                      wizard.batchProductTypeFromScan === LegacyProductCode.BC
                    }
                  />
                ) : null}
              </WizardShell>
            </AppPageLayout>
          </div>
        </AppPageContent>
      </AppIonPage>

      {!isNative ? (
        <ProductScanModal
          open={wizard.showScanModal}
          onOpenChange={wizard.setShowScanModal}
          onResult={async (result) => {
            await wizard.handleScanResult(
              result
                ? { hash: result.hash, productType: result.productType }
                : null,
            );
            wizard.setShowScanModal(false);
          }}
          title={productsSetupDict?.scanQr}
          description={productsSetupDict?.scanQrDescription}
          cancelLabel={productsSetupDict?.cancel}
          cameraErrorLabel={productsSetupDict?.scanCameraError}
          cameraErrorHint={productsSetupDict?.scanCameraErrorHint}
          cameraErrorInsecureLabel={productsSetupDict?.scanCameraErrorInsecure}
          cameraErrorPermissionLabel={
            isCapacitorNative()
              ? productsSetupDict?.scanCameraErrorPermission
              : productsSetupDict?.scanCameraErrorPermissionBrowser
          }
          cameraErrorNotFoundLabel={productsSetupDict?.scanCameraErrorNotFound}
          cameraLoadingLabel={productsSetupDict?.scanCameraLoading}
          cameraSelectLabel={productsSetupDict?.scanCameraSelect}
          emptyCodeLabel={productsSetupDict?.scanEmptyCode}
          invalidCodeLabel={productsSetupDict?.scanInvalidCode}
          retryLabel={productsSetupDict?.scanRetry}
          enterManuallyLabel={productsSetupDict?.enterManually}
          onEnterManually={() => {
            wizard.setShowScanModal(false);
            setManualCodeOpen(true);
          }}
        />
      ) : null}

      <ProductsSetupWizardDialogs
        productsSetupDict={productsSetupDict}
        resetConfirmPayload={wizard.resetConfirmPayload}
        onResetConfirmOpenChange={(open) => {
          if (!open) wizard.setResetConfirmPayload(null);
        }}
        onResetConfirm={() => {
          if (wizard.resetConfirmPayload) {
            wizard.releaseMutation.mutate(wizard.resetConfirmPayload);
          }
        }}
        isResetPending={wizard.releaseMutation.isPending}
        showAbortConfirm={wizard.showAbortConfirm}
        onAbortConfirmOpenChange={wizard.setShowAbortConfirm}
        onAbortConfirm={wizard.handleAbortConfirm}
      />
    </>
  );
}
